import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  Button,
  TextField,
  Pagination,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useECSContext } from "../../../ecsContext";

export default function Notes() {
  const { mrqNotes, setMrqNotes } = useECSContext();

  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuNoteIndex, setMenuNoteIndex] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newNoteContent, setNewNoteContent] = useState("");

  // Pagination
  const itemsPerPage = 3;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(mrqNotes.length / itemsPerPage);

  const paginatedNotes = mrqNotes.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  // Menu handlers
  const handleOpenMenu = (event, index) => {
    setAnchorEl(event.currentTarget);
    setMenuNoteIndex(index);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuNoteIndex(null);
  };

  // Selection
  const toggleSelect = (index) => {
    setSelected((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const deleteSelected = () => {
    setMrqNotes((prev) => prev.filter((_, i) => !selected.includes(i)));
    setSelected([]);
  };

  const deleteNote = (index) => {
    setMrqNotes((prev) => prev.filter((_, i) => i !== index));
    handleCloseMenu();
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    handleCloseMenu();
  };

  const saveEdit = (index) => {
    if (newNoteContent.trim()) {
      setMrqNotes((prev) =>
        prev.map((note, i) => (i === index ? newNoteContent.trim() : note)),
      );
    }
    setEditingIndex(null);
    setNewNoteContent("");
  };

  const addNewNote = () => {
    if (newNoteContent.trim()) {
      setMrqNotes((prev) => [...prev, newNoteContent.trim()]);
      setNewNoteContent("");
      setShowForm(false);
    }
  };

  return (
    <Box sx={{ p: 3, pt: 0, width: "100%" }}>
      {/* Header - Delete selected */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        {selected.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={deleteSelected}
          >
            Delete {selected.length} selected
          </Button>
        )}
      </Box>

      {/* Notes List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {paginatedNotes.map((note, idx) => {
          const globalIndex = (page - 1) * itemsPerPage + idx;

          return (
            <Card
              key={globalIndex}
              sx={{
                position: "relative",
                borderRadius: 3,
                p: 1,
                boxShadow: 3,
                "&:hover": { boxShadow: 6 },
              }}
            >
              <Checkbox
                checked={selected.includes(globalIndex)}
                onChange={() => toggleSelect(globalIndex)}
                sx={{ position: "absolute", top: 8, left: 8 }}
              />

              <CardContent sx={{ pl: 6, pr: 6 }}>
                {editingIndex === globalIndex ? (
                  <TextField
                    multiline
                    fullWidth
                    autoFocus
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    onBlur={() => saveEdit(globalIndex)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        saveEdit(globalIndex);
                      }
                    }}
                  />
                ) : (
                  <Typography variant="body1">{note}</Typography>
                )}
              </CardContent>

              <IconButton
                onClick={(e) => handleOpenMenu(e, globalIndex)}
                sx={{ position: "absolute", top: 8, right: 8 }}
              >
                <MoreVertIcon />
              </IconButton>
            </Card>
          );
        })}
      </Box>

      {/* Add Note Button / Form */}
      {!showForm ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(true)}
          fullWidth
          sx={{ mt: 2 }}
        >
          Add Note
        </Button>
      ) : (
        <Box
          sx={{
            mt: 4,
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            boxShadow: 2,
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => {
              setShowForm(false);
              setNewNoteContent("");
            }}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" sx={{ mb: 2 }}>
            New Note
          </Typography>

          <TextField
            multiline
            rows={4}
            fullWidth
            autoFocus
            placeholder="Write your note here..."
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
          />

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button variant="outlined" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={addNewNote}>
              Save
            </Button>
          </Box>
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => startEdit(menuNoteIndex)}>Edit</MenuItem>
        <MenuItem onClick={() => deleteNote(menuNoteIndex)}>Delete</MenuItem>
      </Menu>
    </Box>
  );
}
